import React from 'react';
import { Helmet } from 'react-helmet-async';
import {
  useReveal, ServiceHero, StatsRow, ProcessSection, WhatWeDeliver,
  ServiceCaseStudies, ServiceFAQ,
  IndustriesSection, WhyZoneX, ServiceCTA, Footer,
} from './shared/ServicePageSections';

const COLOR = '#5C2DFF';

const PROCESS = [
  {
    num:'01', title:'User Research & Flow Mapping',
    desc:'We begin by understanding who uses your application, what decisions they need to make, and what data they need to make them. This means talking to real users — plant operators, field engineers, executives, or consumers — before defining a single screen. The result is a user flow that reflects how people actually work, not how engineers assume they work.',
    points:[
      'Stakeholder interviews and end-user persona definition',
      'Critical decision and data requirement mapping',
      'Information architecture and navigation flow design',
      'Content priority and screen hierarchy definition',
    ],
  },
  {
    num:'02', title:'UI/UX Design & Prototyping',
    desc:'We translate the user research into wireframes and then interactive prototypes — before a single line of code is written. You see and click through your dashboard before development begins. Feedback at this stage costs an hour; feedback after development costs weeks.',
    points:[
      'Low-fidelity wireframes for all primary screens and flows',
      'High-fidelity UI design with your brand and color system',
      'Interactive prototype for stakeholder review and sign-off',
      'Design system and component library for consistent UI',
    ],
  },
  {
    num:'03', title:'Frontend Development',
    desc:'We build responsive web dashboards in React.js with real-time data streaming, and mobile applications in React Native or Flutter — Android and iOS from a single codebase. Every interface is optimised for the device and network conditions of your end user.',
    points:[
      'React.js web dashboard with real-time WebSocket data streaming',
      'React Native or Flutter cross-platform mobile app',
      'Responsive design — tested on desktop, tablet, and mobile',
      'Low-bandwidth optimisation for Indian network conditions',
    ],
  },
  {
    num:'04', title:'Backend & API Integration',
    desc:'We build the backend APIs that connect your interface to your IoT cloud — REST APIs for device management and historical data, WebSocket streams for live telemetry, and device control endpoints. Role-based access control and user authentication are built in from the start, not added as an afterthought.',
    points:[
      'REST API development for device management and data access',
      'WebSocket integration for real-time live data streaming',
      'Device control endpoints with command acknowledgement',
      'JWT authentication, role-based access control (RBAC), and user management',
    ],
  },
  {
    num:'05', title:'Testing, Launch & Maintenance',
    desc:'We test across devices, browsers, and network conditions — including the low-bandwidth, older-Android-device reality of many Indian deployments. We manage the production launch, then stay engaged with ongoing maintenance, performance monitoring, and feature development as your platform grows.',
    points:[
      'Cross-device and cross-browser functional testing',
      'Load testing, performance optimisation, and CDN setup',
      'Staged deployment with rollback capability',
      'Ongoing maintenance, bug fixes, and feature development support',
    ],
  },
];

const DELIVERABLES = [
  { title:'Custom IoT Web Dashboard', desc:'Responsive React.js dashboard with real-time data, charts, device management, and role-based access — production-ready.' },
  { title:'Mobile Application', desc:'Android and iOS mobile app (React Native or Flutter) for monitoring, device control, and alert management.' },
  { title:'Real-Time Data Visualisation', desc:'Live charts, gauges, maps, and tables that update in real time as your devices report — built on WebSocket streaming.' },
  { title:'Alert & Notification System', desc:'Push notifications, SMS, and email alerting for threshold breaches, device faults, and critical operational events.' },
  { title:'Device Management Panel', desc:'Status overview, remote configuration, OTA firmware trigger, device grouping, and filtering for fleet management.' },
  { title:'Role-Based Access Control', desc:'Multi-tier user roles with custom permissions — admin, operator, viewer, and custom roles for enterprise deployments.' },
  { title:'Historical Data Reports', desc:'Date-range filtering, trend analysis, and CSV/PDF export for compliance reporting and business intelligence.' },
  { title:'API Documentation', desc:'Full API documentation for third-party integrations, enabling future extension of your IoT platform without friction.' },
];

const CASE_STUDIES = [
  {
    tag: 'Energy',
    title: 'Hospital Energy Management Dashboard',
    metric: '22% energy cost reduction',
    desc: 'Real-time React.js dashboard for monitoring 300+ electrical circuits across a multi-building hospital campus — live power consumption charts, anomaly alerts, and monthly PDF compliance reports.',
  },
  {
    tag: 'Logistics',
    title: 'Fleet Tracking Mobile Application',
    metric: '1,200 vehicles tracked live',
    desc: 'Cross-platform React Native app for a fleet management company — live vehicle map, driver behaviour scoring, geofence alerts, and offline-capable trip history for drivers without reliable connectivity.',
  },
  {
    tag: 'Manufacturing',
    title: 'Predictive Maintenance Platform',
    metric: '67% reduction in unplanned downtime',
    desc: 'Multi-plant dashboard with live vibration and temperature feeds from 80+ machine sensors — trend analysis, maintenance scheduling, and automated work-order generation integrated with an existing ERP system.',
  },
];

const FAQS = [
  {
    q: 'What technologies do you use to build IoT dashboards?',
    a: 'We build IoT web dashboards using React.js with real-time data streaming via WebSocket APIs. For mobile applications, we use React Native or Flutter for cross-platform Android and iOS development from a single codebase. Backend APIs are built with Node.js or Python depending on your existing infrastructure.',
  },
  {
    q: 'How do you handle real-time data from thousands of devices simultaneously?',
    a: 'Real-time data is streamed from your IoT cloud backend to the dashboard via WebSocket connections, which maintain persistent connections for live updates without polling. The architecture is designed to handle high message throughput efficiently, with appropriate buffering and update batching for performance.',
  },
  {
    q: 'Can you build a dashboard that works on slow internet connections in rural India?',
    a: 'Yes — this is a core design requirement for many of our clients. We optimise dashboards for low-bandwidth conditions including lazy-loading, data compression, progressive rendering, and graceful degradation. We test specifically on the kind of 2G and patchy 4G conditions that are common in rural and semi-urban India.',
  },
  {
    q: 'Can you integrate the dashboard with our existing ERP, CRM, or other systems?',
    a: 'Yes. We build REST API integration layers that connect your IoT dashboard to existing enterprise systems — ERP, CRM, SCADA, HMS, or any system with an accessible API. We also provide API documentation so future integrations are straightforward.',
  },
  {
    q: 'How long does it take to build an IoT dashboard and mobile app?',
    a: 'A focused IoT dashboard with core monitoring, alerting, and device management features typically takes 8 to 14 weeks from approved wireframes to production launch. A combined web dashboard and mobile app project typically takes 12 to 20 weeks, depending on feature scope, integration complexity, and design approval speed.',
  },
];

function HeroVisual() {
  return (
    <img src="/visuals/services/Service 5.png" alt="IoT Dashboards and Mobile Applications" style={{ width:'100%', display:'block' }} />
  );
}

export default function IoTDashboardsPage() {
  const [heroRef, heroVisible] = useReveal();
  return (
    <>
      <Helmet>
        <title>IoT Dashboard & App Development Company India — Zone-X</title>
        <meta name="description" content="Zone-X builds custom IoT dashboards, web applications, and mobile apps in India — real-time data visualisation, device management, and React Native mobile apps for connected products." />
        <link rel="canonical" href="https://zone-x.dev/iot-dashboards-web-mobile" />
      </Helmet>

      <div ref={heroRef}>
        <ServiceHero
          tag="Services / IoT Dashboards & Apps"
          breadcrumb="Dashboards & Apps"
          title="IoT Dashboards, Web &"
          titleAccent="Mobile Applications"
          subtitle="Your device has a story. Let your dashboard tell it. Most IoT systems fail at the interface layer — not because the hardware broke, but because users couldn't figure out the dashboard. Zone-X builds IoT applications that turn raw device data into clear decisions, on any device, anywhere."
          geo="IoT app development for businesses in Bangalore, Karnataka, and across India."
          color={COLOR}
          visible={heroVisible}
          visual={<HeroVisual />}
        />
      </div>

      <StatsRow />
      <InterfaceProblems color={COLOR} />
      <ProcessSection steps={PROCESS} color={COLOR} />
      <WhatWeDeliver items={DELIVERABLES} color={COLOR} />
      <ServiceCaseStudies studies={CASE_STUDIES} color={COLOR} />
      <ServiceFAQ faqs={FAQS} color={COLOR} />
      <IndustriesSection />
      <WhyZoneX />
      <ServiceCTA />
      <Footer />
    </>
  );
}

function InterfaceProblems({ color }) {
  const [ref, visible] = useReveal();
  const facts = [
    { label:'Most IoT Failures', val:'Interface Layer', desc:'Many IoT deployments fail not because the hardware broke, but because users couldn\'t figure out the dashboard or the app gave them data without context.' },
    { label:'Our Approach', val:'User-First Design', desc:'We design for the actual end user — whether a plant operator, a farmer, or a C-suite executive reviewing KPIs. Research before wireframes, wireframes before code.' },
    { label:'Built for India', val:'Low-Bandwidth Ready', desc:'Optimised for patchy connectivity, lightweight on older Android devices, and tested in real-world Indian network conditions before every deployment.' },
  ];
  return (
    <section style={{ padding:'0 0 clamp(40px,5vw,64px)' }}>
      <div style={{ maxWidth:1280, margin:'0 auto', padding:'0 clamp(20px,5vw,48px)' }}>
        <div ref={ref} style={{ opacity:visible?1:0, transform:visible?'none':'translateY(22px)', transition:'opacity 520ms, transform 520ms' }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16 }} className="facts-three-col">
            {facts.map((f,i) => (
              <div key={f.label} style={{ background:'rgba(255,255,255,0.82)', border:'1px solid rgba(10,10,20,0.08)', borderRadius:20, padding:'clamp(20px,2.4vw,28px)', opacity:visible?1:0, transform:visible?'none':'translateY(20px)', transition:`opacity 480ms ${i*80+80}ms, transform 480ms ${i*80+80}ms` }}>
                <div style={{ fontFamily:"'Anton', sans-serif", fontSize:9.5, color:color, textTransform:'uppercase', marginBottom:8 }}>{f.label}</div>
                <div style={{ fontFamily:"'Anton',sans-serif", fontSize:'clamp(18px,2.2vw,26px)', color:'var(--ink)', marginBottom:10, lineHeight:1.15 }}>{f.val}</div>
                <div style={{ fontSize:13.5, lineHeight:1.72, color:'var(--ink-soft)' }}>{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <style>{`@media(max-width:760px){.facts-three-col{grid-template-columns:1fr!important}}`}</style>
    </section>
  );
}
